import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getBookingById, updateBookingStatus, deleteBooking } from '../../services/supabase';
import { Button } from '../../components/ui/Button';
import { BookingStatus } from '../../types';
import toast from 'react-hot-toast';

// Интерфейс для пользователя
interface User {
    id: string;
    username?: string | null;
}

// Интерфейс для оборудования
interface Equipment {
    id: string;
    name: string;
    daily_rate: number;
    quantity: number;
    sort_order?: number | null;
}

// Интерфейс для элемента бронирования
interface BookingEquipment {
    id: string;
    booking_id: string;
    equipment_id: string;
    quantity: number;
    equipment?: Equipment;
}

// Интерфейс для бронирования
interface Booking {
    id: string;
    user_id: string;
    start_date: string;
    end_date: string;
    status: string | null;
    created_at: string | null;
    updated_at: string | null;
    booking_equipment: BookingEquipment[];
    users?: User | null; // Поле будет добавлено после получения данных
}

const AdminBookingDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [confirmDelete, setConfirmDelete] = useState(false);

    // Получение данных бронирования
    const { data: booking, isLoading, isError } = useQuery({
        queryKey: ['admin', 'booking', id],
        queryFn: async () => {
            const { data, error } = await getBookingById(id as string);
            if (error) throw error;

            // Преобразуем сырые данные в правильный формат
            if (!data) return null;

            const formattedBooking: Booking = {
                id: data.id,
                user_id: data.user_id,
                start_date: data.start_date,
                end_date: data.end_date,
                status: (data.status as BookingStatus) || 'pending',
                created_at: data.created_at || new Date().toISOString(),
                updated_at: data.updated_at,
                booking_equipment: Array.isArray(data.booking_equipment)
                    ? data.booking_equipment.map(item => ({
                        id: item.id,
                        booking_id: item.booking_id,
                        equipment_id: item.equipment_id,
                        quantity: item.quantity,
                        equipment: item.equipment ? {
                            id: item.equipment.id,
                            name: item.equipment.name,
                            daily_rate: item.equipment.daily_rate,
                            quantity: item.equipment.quantity
                        } : undefined
                    }))
                    : []
            };

            return formattedBooking;
        }
    });

    // Мутация для обновления статуса бронирования
    const updateStatusMutation = useMutation({
        mutationFn: (status: BookingStatus) => {
            return updateBookingStatus(id as string, status);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'booking', id] });

            // Инвалидируем кеш доступного оборудования при изменении статуса,
            // особенно важно при установке статуса 'completed'
            queryClient.invalidateQueries({ queryKey: ['availableEquipment'] });
            queryClient.invalidateQueries({ queryKey: ['equipment'] });

            toast.success('Статус бронирования обновлен');
        },
        onError: (error: any) => {
            console.error('Ошибка при обновлении статуса:', error);
            toast.error('Не удалось обновить статус бронирования');
        }
    });

    // Мутация для удаления бронирования
    const deleteBookingMutation = useMutation({
        mutationFn: () => {
            return deleteBooking(id as string);
        },
        onSuccess: () => {
            toast.success('Бронирование удалено');
            navigate('/admin/bookings');
        },
        onError: (error: any) => {
            console.error('Ошибка при удалении бронирования:', error);
            toast.error('Не удалось удалить бронирование');
        }
    });

    // Обработчик изменения статуса
    const handleStatusChange = (status: BookingStatus) => {
        // Проверяем, что мутация не выполняется в данный момент
        if (updateStatusMutation.isPending) {
            console.log('Мутация уже выполняется, игнорируем повторное нажатие');
            return;
        }
        updateStatusMutation.mutate(status);
    };

    // Обработчик удаления бронирования
    const handleDeleteBooking = () => {
        if (window.confirm('Вы уверены, что хотите удалить это бронирование?')) {
            deleteBookingMutation.mutate();
        }
    };

    // Получаем локализованное название статуса
    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'pending': return 'Ожидает';
            case 'confirmed': return 'Подтверждено';
            case 'completed': return 'Завершено';
            case 'cancelled': return 'Отменено';
            default: return status;
        }
    };

    // Получаем цвет для статуса
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'confirmed': return 'bg-green-100 text-green-800';
            case 'completed': return 'bg-blue-100 text-blue-800';
            case 'cancelled': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Безопасный форматировщик даты
    const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return 'Нет данных';
        try {
            return new Date(dateString).toLocaleDateString('ru-RU');
        } catch (e) {
            return 'Неверная дата';
        }
    };

    // Безопасный форматировщик даты и времени
    const formatDateTime = (dateString: string | null | undefined): string => {
        if (!dateString) return 'Нет данных';
        try {
            return new Date(dateString).toLocaleString('ru-RU');
        } catch (e) {
            return 'Неверная дата';
        }
    };

    // Отображаем индикатор загрузки
    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    // Отображаем сообщение об ошибке
    if (isError || !booking) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="bg-red-50 p-4 rounded-md text-red-700 mb-4">
                    Произошла ошибка при загрузке деталей бронирования. Пожалуйста, попробуйте позже.
                </div>
                <Link to="/admin/bookings" className="text-primary-600 hover:underline">
                    ← Вернуться к списку бронирований
                </Link>
            </div>
        );
    }

    // После этой проверки booking гарантированно не null и не undefined
    const userData = booking.users as User | null;

    // Рассчитываем продолжительность аренды в днях
    const startDate = new Date(booking.start_date || '');
    const endDate = new Date(booking.end_date || '');
    const durationDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) || 1;

    // Функция для склонения слова "день" в зависимости от числа
    const getDaysText = (days: number): string => {
        const lastDigit = days % 10;
        const lastTwoDigits = days % 100;

        if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
            return 'дней';
        }

        if (lastDigit === 1) {
            return 'день';
        }

        if (lastDigit >= 2 && lastDigit <= 4) {
            return 'дня';
        }

        return 'дней';
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-semibold text-gray-800">Детали бронирования</h1>
                <Link to="/admin/bookings" className="text-primary-600 hover:underline">
                    ← Вернуться к списку бронирований
                </Link>
            </div>

            {/* Основная информация */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* Информация о бронировании */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h2 className="text-lg font-medium mb-4 text-gray-800">Информация о бронировании</h2>
                    <div className="space-y-3">
                        <div>
                            <span className="text-sm text-gray-500">ID бронирования:</span>
                            <p className="font-mono text-sm">{booking.id}</p>
                        </div>
                        <div>
                            <span className="text-sm text-gray-500">Даты аренды:</span>
                            <p className="font-medium">
                                {formatDate(booking.start_date)} — {formatDate(booking.end_date)}
                            </p>
                            <p className="text-sm text-gray-500">
                                ({durationDays} {getDaysText(durationDays)})
                            </p>
                        </div>
                        <div>
                            <span className="text-sm text-gray-500">Статус:</span>
                            <p>
                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(booking.status || '')}`}>
                                    {getStatusLabel(booking.status || '')}
                                </span>
                            </p>
                        </div>
                        <div>
                            <span className="text-sm text-gray-500">Дата создания:</span>
                            <p>{formatDateTime(booking.created_at)}</p>
                        </div>
                    </div>
                </div>

                {/* Информация о клиенте */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h2 className="text-lg font-medium mb-4 text-gray-800">Информация о клиенте</h2>
                    <div className="space-y-3">
                        {userData ? (
                            <>
                                <div>
                                    <span className="text-sm text-gray-500">Пользователь:</span>
                                    <p className="font-medium">
                                        {userData.username || 'Имя не указано'}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">ID пользователя:</span>
                                    <p className="font-mono text-sm">{userData.id}</p>
                                </div>
                            </>
                        ) : (
                            <p className="text-gray-500">Информация о клиенте недоступна</p>
                        )}
                    </div>
                </div>

                {/* Действия */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h2 className="text-lg font-medium mb-4 text-gray-800">Действия</h2>
                    <div className="space-y-3">
                        <div>
                            <span className="text-sm text-gray-500 block mb-2">Изменить статус:</span>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => handleStatusChange('pending')}
                                    disabled={booking.status === 'pending' || updateStatusMutation.isPending}
                                    className={`px-3 py-1 rounded-md text-sm font-medium ${booking.status === 'pending'
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : updateStatusMutation.isPending
                                                ? 'bg-yellow-100 text-yellow-800 opacity-70 cursor-not-allowed'
                                                : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                        }`}
                                >
                                    {updateStatusMutation.isPending && updateStatusMutation.variables === 'pending'
                                        ? '...'
                                        : 'Ожидает'}
                                </button>
                                <button
                                    onClick={() => handleStatusChange('confirmed')}
                                    disabled={booking.status === 'confirmed' || updateStatusMutation.isPending}
                                    className={`px-3 py-1 rounded-md text-sm font-medium ${booking.status === 'confirmed'
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : updateStatusMutation.isPending
                                                ? 'bg-green-100 text-green-800 opacity-70 cursor-not-allowed'
                                                : 'bg-green-100 text-green-800 hover:bg-green-200'
                                        }`}
                                >
                                    {updateStatusMutation.isPending && updateStatusMutation.variables === 'confirmed'
                                        ? '...'
                                        : 'Подтверждено'}
                                </button>
                                <button
                                    onClick={() => handleStatusChange('completed')}
                                    disabled={booking.status === 'completed' || updateStatusMutation.isPending}
                                    className={`px-3 py-1 rounded-md text-sm font-medium ${booking.status === 'completed'
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : updateStatusMutation.isPending
                                                ? 'bg-blue-100 text-blue-800 opacity-70 cursor-not-allowed'
                                                : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                        }`}
                                >
                                    {updateStatusMutation.isPending && updateStatusMutation.variables === 'completed'
                                        ? '...'
                                        : 'Завершено'}
                                </button>
                                <button
                                    onClick={() => handleStatusChange('cancelled')}
                                    disabled={booking.status === 'cancelled' || updateStatusMutation.isPending}
                                    className={`px-3 py-1 rounded-md text-sm font-medium ${booking.status === 'cancelled'
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : updateStatusMutation.isPending
                                                ? 'bg-red-100 text-red-800 opacity-70 cursor-not-allowed'
                                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                                        }`}
                                >
                                    {updateStatusMutation.isPending && updateStatusMutation.variables === 'cancelled'
                                        ? '...'
                                        : 'Отменено'}
                                </button>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-gray-200 mt-4">
                            {!confirmDelete ? (
                                <Button
                                    variant="destructive"
                                    onClick={() => setConfirmDelete(true)}
                                    className="w-full"
                                >
                                    Удалить бронирование
                                </Button>
                            ) : (
                                <div className="space-y-2">
                                    <p className="text-sm text-red-600 font-medium">Вы уверены?</p>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="destructive"
                                            onClick={handleDeleteBooking}
                                            className="flex-1"
                                        >
                                            Да, удалить
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => setConfirmDelete(false)}
                                            className="flex-1"
                                        >
                                            Отмена
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Список оборудования */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
                <h2 className="text-lg font-medium mb-4 text-gray-800">Забронированное оборудование</h2>

                {booking.booking_equipment && booking.booking_equipment.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Наименование
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Количество
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Цена за день
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Сумма
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {booking.booking_equipment.map((item, index) => (
                                    <tr key={index}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {item.equipment?.name || 'Неизвестное оборудование'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.quantity} шт.
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.equipment?.daily_rate
                                                ? `${item.equipment.daily_rate.toLocaleString()} ₽`
                                                : 'N/A'
                                            }
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {item.equipment?.daily_rate
                                                ? `${(item.equipment.daily_rate * item.quantity * durationDays).toLocaleString()} ₽`
                                                : 'N/A'
                                            }
                                        </td>
                                    </tr>
                                ))}
                                <tr className="bg-gray-50">
                                    <td colSpan={3} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                                        Итого за {durationDays} {getDaysText(durationDays)}:
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        {booking.booking_equipment.reduce((sum, item) => {
                                            const dailyRate = item.equipment?.daily_rate || 0;
                                            return sum + (dailyRate * item.quantity * durationDays);
                                        }, 0).toLocaleString()} ₽
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-gray-500">Нет данных о забронированном оборудовании</p>
                )}
            </div>
        </div>
    );
};

export default AdminBookingDetail; 